import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  Badge,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  AttachFile,
  CheckCircle,
  Check,
  Warning,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useTranslation } from '@/core/i18n/i18n';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import {
  ChecklistWithRelations,
  ChecklistSavedResponse,
  ChecklistWithRelationsData,
} from '@/core/types/state';
import { useDispatch } from 'react-redux';
import { saveChecklistResponses as saveChecklistResponsesAction } from '@/core/slices/checklists';
import Navigation from '../Shared/Navigation.tsx';
import { shouldShowSelfAssessmentResultsStep } from './resultsStepAvailability';
import {
  AnnualPlanComplianceContext,
  PlanValidation,
  PERIOD_TYPES,
  TimePeriodData,
} from '@shared/schema';
import { useAnyLoading } from '@/core/useAnyLoading';

interface ChecklistProps {
  currentPlan?: unknown;
  currentVersion?: number | null;
  currentFulfillmentVersion?: number | null;
  checklists: ChecklistWithRelations[];
  readOnlyMode?: boolean | null;
  isLoading: boolean;
  periods: TimePeriodData[];
  errorMessage: string | null;
  fetchAnnualPlanComplianceContext: (
    versionId: number | null | undefined,
    fulfillmentVersionId: number,
  ) => Promise<AnnualPlanComplianceContext | null>;
  responses?: ChecklistSavedResponse[];
  selectedYear: string | number | null;
  scopeName: string;
  currentBusinessUnit?: string | null;
  uploadChecklistAnswerEvidence?: (file: File) => Promise<{ name: string; url: string } | null>;
  resolveChecklistAnswerEvidence?: (name: string) => Promise<{ name: string; url: string } | null>;
  markFulfillmentVersionForApproval: (fulfillmentVersionId: number) => Promise<void>;
  enterpriseName: string;
  planValidation?: PlanValidation | null | undefined;
  getPlanValidation: (
    fulfillmentVersionId: number,
  ) => Promise<PlanValidation | null | undefined> | PlanValidation | null | undefined;
}

const formatPercentage = (value: number | null | undefined, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Number(num.toFixed(digits));
  const safe = rounded === 0 ? 0 : rounded;
  const str = safe.toFixed(digits);
  return str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const Checklist: React.FC<ChecklistProps> = ({
  currentPlan: _currentPlan,
  currentVersion,
  currentFulfillmentVersion,
  readOnlyMode,
  checklists: incomingChecklists,
  isLoading: _isLoading,
  errorMessage: _errorMessage,
  fetchAnnualPlanComplianceContext,
  selectedYear,
  responses = [],
  uploadChecklistAnswerEvidence,
  resolveChecklistAnswerEvidence: _resolveChecklistAnswerEvidence,
  markFulfillmentVersionForApproval,
  periods,
  scopeName,
  currentBusinessUnit,
  enterpriseName: _enterpriseName,
  planValidation,
  getPlanValidation,
}) => {
  const isAnyLoading = useAnyLoading();
  const dispatch = useDispatch();
  const runThunk = <T,>(thunk: unknown): Promise<T> =>
    (dispatch as unknown as (t: unknown) => Promise<T>)(thunk);
  const { t, tr } = useTranslation();
  const { id: _id } = useParams();
  const navigate = useNavigate();
  const [expandedChecklist, setExpandedChecklist] = useState<string | false>(false);
  const [expandedQuestion, setExpandedQuestion] = useState<string | false>(false);
  const checklistData: ChecklistWithRelations[] = incomingChecklists;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  interface LocalFocusProgress {
    APFocusId: number;
    Name: string;
    Weight?: number | null;
    FulfillmentProgressPct?: number | null;
  }
  const [focusesProgress, setFocusesProgress] = useState<LocalFocusProgress[]>([]);
  const [globalFulfillmentPct, setGlobalFulfillmentPct] = useState<number>(0);

  interface LocalQuestion {
    id: string;
    text: string;
    selectedOptionId: number | null;
    comment: string;
    evidence: File[];
    evidenceName?: string | null;
    evidenceUrl?: string | null;
    options: { id: number; label: string; weight: number }[];
    questionWeight?: number;
  }
  interface LocalCustomField {
    id: number;
    name: string;
    sort: number | null;
    value: string;
  }
  interface LocalChecklist {
    id: string;
    name: string;
    code: string;
    questions: LocalQuestion[];
    progress: number;
    APChecklistId?: number | null;
    typeId?: number | null;
    typeName?: string | null;
    customFields: LocalCustomField[];
  }
  const [localChecklists, setLocalChecklists] = useState<LocalChecklist[]>([]);
  const hasEvidence = React.useCallback((question: LocalQuestion) => {
    const evidenceName =
      typeof question.evidenceName === 'string' ? question.evidenceName.trim() : '';
    const evidenceUrl = typeof question.evidenceUrl === 'string' ? question.evidenceUrl.trim() : '';
    const evidenceFiles = Array.isArray(question.evidence) ? question.evidence : [];
    return Boolean(evidenceName || evidenceUrl || evidenceFiles.length > 0);
  }, []);

  const isQuestionComplete = React.useCallback(
    (question: LocalQuestion) => {
      if (question.selectedOptionId === null) return false;
      const selectedOption = question.options.find((opt) => opt.id === question.selectedOptionId);
      const selectedOptionWeight = selectedOption?.weight ?? 0;
      if (selectedOptionWeight > 0) {
        return hasEvidence(question);
      }
      return true;
    },
    [hasEvidence],
  );
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedCustomFields, setExpandedCustomFields] = useState<Record<string, boolean>>({});

  const isCustomFieldValueComplete = React.useCallback((field: LocalCustomField) => {
    if (!field) return true;
    const trimmed = `${field.value ?? ''}`.trim();
    if (!trimmed) return false;
    const numericValue = Number(trimmed);
    if (!Number.isFinite(numericValue)) return false;
    return numericValue >= 0;
  }, []);

  const areCustomFieldsComplete = React.useCallback(
    (checklist: LocalChecklist) =>
      checklist.customFields.every((field) => isCustomFieldValueComplete(field)),
    [isCustomFieldValueComplete],
  );

  const isChecklistQuestionsComplete = React.useCallback(
    (checklist: LocalChecklist) => {
      if (!checklist.questions || checklist.questions.length === 0) return true;
      return checklist.questions.every((question) => isQuestionComplete(question));
    },
    [isQuestionComplete],
  );

  const normalizeToUTCDate = (dateString: string) => {
    const d = new Date(dateString);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  };

  const isInSelfAssessmentPeriod = () => {
    if (!periods || periods.length === 0) return false;
    const now = new Date();
    const currentDate = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );
    const validNames = [PERIOD_TYPES.SELF_ASSESSMENT, PERIOD_TYPES.SELF_ASSESSMENT_MODIFICATION];
    return periods.some(
      (period) =>
        validNames.includes(period.Name) &&
        normalizeToUTCDate(period.StartDate) <= currentDate &&
        normalizeToUTCDate(period.EndDate) >= currentDate,
    );
  };

  useEffect(() => {
    if (readOnlyMode && currentFulfillmentVersion && !planValidation) {
      getPlanValidation(currentFulfillmentVersion);
    }
  }, [currentFulfillmentVersion, getPlanValidation, planValidation, readOnlyMode]);

  useEffect(() => {
    let cancelled = false;
    const loadComplianceContext = async () => {
      if (!currentFulfillmentVersion) return;
      try {
        const context = await fetchAnnualPlanComplianceContext(
          currentVersion,
          currentFulfillmentVersion,
        );
        if (cancelled) return;
        if (!context) {
          setFocusesProgress([]);
          setGlobalFulfillmentPct(0);
          return;
        }
        setFocusesProgress(
          context.focusProgress.map((f) => ({
            APFocusId: f.APFocusId,
            Name: f.Name,
            Weight: f.Weight ?? null,
            FulfillmentProgressPct: f.FulfillmentProgressPct ?? null,
          })),
        );
        setGlobalFulfillmentPct(context.globalFulfillmentProgressPct || 0);
      } catch (e) {
        console.error('Error fetching compliance context (checklist view):', e);
        setFocusesProgress([]);
        setGlobalFulfillmentPct(0);
      }
    };
    loadComplianceContext();
    return () => {
      cancelled = true;
    };
  }, [currentFulfillmentVersion, currentVersion, fetchAnnualPlanComplianceContext]);

  useEffect(() => {
    if (checklistData.length === 0) {
      setLocalChecklists([]);
      return;
    }
    const data: ChecklistWithRelationsData[] = checklistData.flatMap(
      (scope) => scope.Checklists || [],
    );
    const mapped = data.map((c: ChecklistWithRelationsData) => ({
      id: c.ChecklistId?.toString(),
      name: c.Name,
      code: c.ChecklistId?.toString(),
      APChecklistId: c.APChecklistId ?? null,
      typeId: c.CategoryId ?? null,
      typeName: c.CategoryName ?? null,
      customFields: (c.CustomFields || []).map((field) => ({
        id: field.CustomFieldId,
        name: field.Name,
        sort: field.Sort ?? null,
        value: '',
      })),
      questions: c.Questions?.map((q) => ({
        id: q.ChecklistQuestionId.toString(),
        text: q.Question,
        selectedOptionId: null,
        comment: '',
        evidence: [],
        evidenceName: null,
        evidenceUrl: null,
        questionWeight: q.QuestionWeightFloat || 0,
        options: (q.Options || []).map((o) => ({
          id: o.ChecklistOptionId,
          label: o.Name,
          weight:
            typeof o.OptionWeightFloat === 'number'
              ? o.OptionWeightFloat
              : parseFloat(o.OptionWeightFloat as string) || 0,
        })),
      })),
      progress: 0,
    }));
    setLocalChecklists(mapped);
  }, [checklistData]);

  useEffect(() => {
    if (!responses || responses.length === 0) return;
    setLocalChecklists((prev) =>
      prev.map((chk) => {
        const related = responses.filter((r) => r.ChecklistId === parseInt(chk.id));
        if (!related.length) return chk;
        const latest = related.sort((a, b) => b.APChecklistResponseId - a.APChecklistResponseId)[0];
        if (!latest) return chk;
        const answeredQuestions = new Set<number>();
        const updatedQuestions = chk.questions.map((q) => {
          const answer = (latest.Answers || []).find(
            (ans) => ans.ChecklistQuestionId === parseInt(q.id),
          );
          if (answer) {
            answeredQuestions.add(parseInt(q.id));
            return {
              ...q,
              selectedOptionId: answer.ChecklistOptionId,
              comment: answer.AnswerComment || '',
              evidenceName: answer.AnswerPhoto || null,
              evidenceUrl: answer.AnswerUrl || null,
            };
          }
          return q;
        });
        const updatedCustomFields = chk.customFields.map((field) => {
          const savedValue = (latest.CustomFieldValues || []).find(
            (stored) => stored.CustomFieldId === field.id,
          );
          if (!savedValue) {
            return { ...field };
          }
          const numericValueRaw =
            typeof savedValue.NumericValue === 'number'
              ? savedValue.NumericValue
              : Number(savedValue.NumericValue);
          const isValidNumber =
            typeof numericValueRaw === 'number' && Number.isFinite(numericValueRaw);
          return {
            ...field,
            value: isValidNumber ? numericValueRaw.toString() : '',
          };
        });
        let totalPossibleWeight = 0;
        let achievedWeight = 0;

        updatedQuestions.forEach((question) => {
          const questionWeight = question.questionWeight || 1;
          const maxOptionWeight = Math.max(...question.options.map((opt) => opt.weight || 0), 0);
          totalPossibleWeight += questionWeight * maxOptionWeight;

          if (isQuestionComplete(question)) {
            const selectedOption = question.options.find(
              (opt) => opt.id === question.selectedOptionId,
            );
            const selectedOptionWeight = selectedOption?.weight || 0;
            achievedWeight += questionWeight * selectedOptionWeight;
          }
        });

        const progress =
          totalPossibleWeight > 0
            ? Math.round((achievedWeight / totalPossibleWeight + Number.EPSILON) * 100)
            : 0;

        return { ...chk, questions: updatedQuestions, customFields: updatedCustomFields, progress };
      }),
    );
  }, [responses, isQuestionComplete]);

  const handleChecklistChange = (checklistId: string) => (_: unknown, isExpanded: boolean) => {
    setExpandedChecklist(isExpanded ? checklistId : false);
  };

  const handlePreguntaChange = (preguntaId: string) => (_: unknown, isExpanded: boolean) => {
    setExpandedQuestion(isExpanded ? preguntaId : false);
  };

  const handleCustomFieldsAccordionChange =
    (checklistId: string) => (_: unknown, isExpanded: boolean) => {
      setExpandedCustomFields((prev) => ({
        ...prev,
        [checklistId]: isExpanded,
      }));
    };

  const handleCustomFieldChange = (checklistId: string, fieldId: number, nextValue: string) => {
    setLocalChecklists((prev) =>
      prev.map((chk) =>
        chk.id === checklistId
          ? {
              ...chk,
              customFields: chk.customFields.map((field) =>
                field.id === fieldId ? { ...field, value: nextValue } : field,
              ),
            }
          : chk,
      ),
    );
  };

  const handleExit = () => {
    navigate('/');
  };

  const handleRespuestaChange = (
    checklistId: string,
    preguntaId: string,
    optionId: number | '',
  ) => {
    setLocalChecklists((prev) =>
      prev.map((chk) =>
        chk.id === checklistId
          ? {
              ...chk,
              questions: chk.questions.map((p) =>
                p.id === preguntaId
                  ? { ...p, selectedOptionId: optionId === '' ? null : Number(optionId) }
                  : p,
              ),
            }
          : chk,
      ),
    );
    updateChecklistAvance(checklistId);
  };

  const handleComentarioChange = (checklistId: string, preguntaId: string, comentario: string) => {
    setLocalChecklists((prev) =>
      prev.map((chk) =>
        chk.id === checklistId
          ? {
              ...chk,
              questions: chk.questions.map((p) =>
                p.id === preguntaId ? { ...p, comment: comentario } : p,
              ),
            }
          : chk,
      ),
    );
  };

  const handleFileUpload = async (
    checklistId: string,
    preguntaId: string,
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file) return;
    if (uploadChecklistAnswerEvidence) {
      try {
        const result = await uploadChecklistAnswerEvidence(file);
        if (result) {
          setLocalChecklists((prev) =>
            prev.map((chk) =>
              chk.id === checklistId
                ? {
                    ...chk,
                    questions: chk.questions.map((p) =>
                      p.id === preguntaId
                        ? {
                            ...p,
                            evidence: [file],
                            evidenceName: result.name,
                            evidenceUrl: result.url,
                          }
                        : p,
                    ),
                  }
                : chk,
            ),
          );
          updateChecklistAvance(checklistId);
        }
      } catch (e) {
        console.error('Error uploading checklist evidence', e);
      }
    }
  };

  const handleDeleteEvidencias = (checklistId: string, preguntaId: string) => {
    setLocalChecklists((prev) =>
      prev.map((chk) =>
        chk.id === checklistId
          ? {
              ...chk,
              questions: chk.questions.map((p) =>
                p.id === preguntaId
                  ? { ...p, evidence: [], evidenceName: null, evidenceUrl: null }
                  : p,
              ),
            }
          : chk,
      ),
    );
    updateChecklistAvance(checklistId);
  };

  const updateChecklistAvance = (checklistId: string) => {
    setLocalChecklists((prev) => {
      return prev.map((checklist) => {
        if (checklist.id !== checklistId) return checklist;

        let totalPossibleWeight = 0;
        let achievedWeight = 0;

        checklist.questions.forEach((question) => {
          const questionWeight = question.questionWeight || 1;

          const maxOptionWeight = Math.max(...question.options.map((opt) => opt.weight || 0), 0);

          totalPossibleWeight += questionWeight * maxOptionWeight;

          if (isQuestionComplete(question)) {
            const selectedOption = question.options.find(
              (opt) => opt.id === question.selectedOptionId,
            );
            const selectedOptionWeight = selectedOption?.weight || 0;
            achievedWeight += questionWeight * selectedOptionWeight;
          }
        });

        const avance =
          totalPossibleWeight > 0
            ? Math.round((achievedWeight / totalPossibleWeight + Number.EPSILON) * 100)
            : 0;

        return { ...checklist, progress: avance };
      });
    });
  };

  const hasIncompleteQuestions = React.useMemo(
    () => localChecklists.some((checklist) => !isChecklistQuestionsComplete(checklist)),
    [isChecklistQuestionsComplete, localChecklists],
  );

  const hasIncompleteCustomFields = React.useMemo(
    () => localChecklists.some((checklist) => !areCustomFieldsComplete(checklist)),
    [areCustomFieldsComplete, localChecklists],
  );

  const disableNext =
    (!readOnlyMode && !isInSelfAssessmentPeriod()) ||
    (!readOnlyMode && (hasIncompleteQuestions || hasIncompleteCustomFields));

  const disableNextReason =
    !readOnlyMode && !isInSelfAssessmentPeriod()
      ? t('checklist.disableReasonOutsideWindow')
      : !readOnlyMode && hasIncompleteCustomFields
        ? t('checklist.disableReasonIncompleteCustomFields')
        : !readOnlyMode && hasIncompleteQuestions
          ? t('checklist.disableReasonIncompleteQuestions')
          : undefined;

  const showResultsStep = React.useMemo(
    () =>
      shouldShowSelfAssessmentResultsStep(readOnlyMode, planValidation?.Approved, selectedYear),
    [planValidation?.Approved, readOnlyMode, selectedYear],
  );
  const handleGoToResults = React.useCallback(() => {
    if (!showResultsStep) return;
    navigate('/self-assessment-results');
  }, [navigate, showResultsStep]);

  const stepGridSize = showResultsStep ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6, md: 4 };

  type ChecklistPayload = Parameters<typeof saveChecklistResponsesAction>[0][number];

  const buildChecklistResponsePayload = (
    checklist: LocalChecklist,
    versionId: number,
  ): ChecklistPayload | null => {
    const answers = checklist.questions
      .map((p) => ({
        questionId: parseInt(p.id, 10),
        optionId: p.selectedOptionId,
        comment: p.comment,
        photo: p.evidenceName || null,
      }))
      .filter((answer) => answer.optionId !== null || answer.comment || answer.photo);

    const hasConfiguredCustomFields = checklist.customFields.length > 0;
    const customFields = hasConfiguredCustomFields
      ? checklist.customFields
          .map((field) => {
            const trimmed = `${field.value ?? ''}`.trim();
            if (!trimmed) return null;
            const numericValue = Number(trimmed);
            if (!Number.isFinite(numericValue)) return null;
            if (numericValue < 0) return null;
            return { customFieldId: field.id, value: numericValue };
          })
          .filter((field): field is { customFieldId: number; value: number } => field !== null)
      : [];

    if (answers.length === 0 && customFields.length === 0) {
      return null;
    }

    return {
      APFulfillmentsVersionId: versionId,
      checklistId: parseInt(checklist.id, 10),
      APChecklistId: checklist.APChecklistId ?? null,
      comment: '',
      isFinished: false,
      answers,
      customFields,
    };
  };

  const handleBack = () => {
    navigate(`/enter-compliance`);
  };

  const handleContinue = () => {
    if (readOnlyMode) {
      navigate('/ceo-validation');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!currentFulfillmentVersion) return;
    const payload = localChecklists
      .map((checklist) => buildChecklistResponsePayload(checklist, currentFulfillmentVersion))
      .filter((item): item is ChecklistPayload => item !== null);

    try {
      await runThunk(saveChecklistResponsesAction(payload));
      await markFulfillmentVersionForApproval(currentFulfillmentVersion);
      setShowConfirmModal(false);
      navigate('/');
    } catch (e) {
      console.error('Error guardando checklists:', e);
    }
  };

  const handleSaveProgress = async () => {
    if (!currentFulfillmentVersion) return;
    const payload = localChecklists
      .map((checklist) => buildChecklistResponsePayload(checklist, currentFulfillmentVersion))
      .filter((item): item is ChecklistPayload => item !== null);

    try {
      await runThunk(saveChecklistResponsesAction(payload));
      setShowConfirmModal(false);
      navigate('/');
    } catch (e) {
      console.error('Error guardando checklists:', e);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: { xs: 2, md: 4 } }}>
      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto', width: '100%' }}>
        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{
              pt: 2,
              px: { xs: 2, md: 3 },
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              rowGap: { xs: 2, md: 0 },
            }}
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid
                container
                spacing={2}
                sx={{
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  rowGap: { xs: 1.5, sm: 0 },
                }}
              >
                {/* Bloque Plan Anual */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Bloque Ámbito */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('common.self_assessment')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={PlanAnualIcon} alt="Plan Anual" style={{ width: 70, height: 70 }} />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper */}
          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 1
                      </Typography>
                      <Typography variant="caption">
                        {t('enterCompliance.enterComplianceData')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 2
                      </Typography>
                      <Typography variant="caption">
                        {t('checklist.maturityLevelEvaluation')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">3</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 3
                      </Typography>
                      <Typography variant="caption">
                        {t('checklist.selfAssessmentAndSignature')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {showResultsStep && (
                <Grid size={stepGridSize}>
                  <Paper
                    elevation={0}
                    onClick={handleGoToResults}
                    sx={{
                      flex: 1,
                      bgcolor: '#f5f5f5',
                      color: '#999',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      height: '100%',
                      cursor: 'pointer',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size="auto">
                        <Grid
                          container
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            color: '#999',
                            fontWeight: 'bold',
                          }}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Grid size="auto">4</Grid>
                        </Grid>
                      </Grid>
                      <Grid size="auto">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('common.step')} 4
                        </Typography>
                        <Typography variant="caption">{t('ceoValidation.step4Caption')}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
            {/* Checklists */}
            {/* {t("approvalDefinitions.complianceByStrategicFocuses")} */}
            {/* Group checklists by type/category */}
            {Object.values(
              localChecklists.reduce<
                Record<string, { typeName: string; checklists: LocalChecklist[] }>
              >((acc, chk) => {
                const key = (chk.typeId ?? 'null').toString();
                if (!acc[key]) {
                  acc[key] = {
                    typeName: chk.typeName || t('checklist.noCategory'),
                    checklists: [],
                  };
                }
                acc[key].checklists.push(chk);
                return acc;
              }, {}),
            ).map(({ typeName, checklists }) => {
              const catKey = typeName + '_' + (checklists[0]?.typeId ?? 'null');
              const isExpanded = expandedCategories[catKey] ?? true;
              return (
                <Accordion
                  key={catKey}
                  expanded={isExpanded}
                  onChange={() =>
                    setExpandedCategories((prev) => ({
                      ...prev,
                      [catKey]: !isExpanded,
                    }))
                  }
                  sx={{
                    mb: 2,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: '#F4F8F8',
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      minHeight: '40px !important',
                      height: '40px !important',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        margin: '8px 0 !important',
                      },
                      '&.Mui-expanded': {
                        minHeight: '40px !important',
                        height: '40px !important',
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '8px 0 !important',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {typeName}
                    </Typography>
                  </AccordionSummary>
                  {isExpanded && (
                    <AccordionDetails
                      sx={{
                        bgcolor: 'white',
                        p: 1,
                        mt: 1,
                        borderRadius: 1,
                        border: '1px solid #A5B2BB',
                      }}
                    >
                      <Box sx={{ p: 1 }}>
                        {checklists.map((checklist) => {
                          const questionAnswers =
                            checklist.questions?.filter((question) => isQuestionComplete(question))
                              .length || 0;
                          const questionCount = checklist.questions?.length || 0;
                          const questionsComplete = isChecklistQuestionsComplete(checklist);
                          const customFieldList = checklist.customFields;
                          const hasCustomFields = customFieldList.length > 0;
                          const customFieldsComplete = areCustomFieldsComplete(checklist);
                          const checklistComplete = questionsComplete && customFieldsComplete;
                          const answeredCount =
                            questionAnswers + (hasCustomFields && customFieldsComplete ? 1 : 0);
                          const totalQuestions = questionCount + (hasCustomFields ? 1 : 0);
                          const groupedCustomFields = customFieldList.reduce<
                            Record<number, LocalCustomField[]>
                          >((acc, field) => {
                            const sortValue =
                              field.sort !== null && field.sort !== undefined
                                ? field.sort
                                : Number.MAX_SAFE_INTEGER;
                            if (!acc[sortValue]) acc[sortValue] = [];
                            acc[sortValue].push(field);
                            return acc;
                          }, {});
                          const orderedCustomFieldGroups = Object.entries(groupedCustomFields)
                            .map(([sortKey, fields]) => ({
                              sort: Number(sortKey),
                              fields: [...fields].sort((a, b) => a.name.localeCompare(b.name)),
                            }))
                            .sort((a, b) => a.sort - b.sort);
                          const answeredSummaryComplete =
                            totalQuestions > 0 ? answeredCount === totalQuestions : false;

                          return (
                            <Accordion
                              key={checklist.id}
                              expanded={expandedChecklist === checklist.id}
                              onChange={handleChecklistChange(checklist.id)}
                              sx={{
                                mb: 2,
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{
                                  bgcolor: '#F4F8F8',
                                  borderRadius: 1,
                                  border: '1px solid #A5B2BB',
                                  minHeight: 'auto',
                                  height: 'auto',
                                  '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    margin: '8px 0 !important',
                                  },
                                  '&.Mui-expanded': {
                                    minHeight: 'auto',
                                    height: 'auto',
                                  },
                                  '& .MuiAccordionSummary-content.Mui-expanded': {
                                    margin: '8px 0 !important',
                                  },
                                }}
                              >
                                <Grid
                                  container
                                  alignItems="center"
                                  justifyContent={{ xs: 'center', md: 'space-between' }}
                                  spacing={1.5}
                                  sx={{ width: '100%', pr: { xs: 0, md: 2 }, flexWrap: 'wrap' }}
                                >
                                  <Grid size={{ xs: 12, md: 'auto' }}>
                                    <Grid container alignItems="center" spacing={2}>
                                      <Grid size="auto">
                                        <CheckCircle
                                          sx={{
                                            color: checklistComplete ? '#4caf50' : '#A5B2BB',
                                          }}
                                        />
                                      </Grid>
                                      <Grid size="auto">
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                          {checklist.name}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    size={{ xs: 12, md: 'auto' }}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                      width: '100%',
                                    }}
                                  >
                                    <Grid
                                      container
                                      alignItems="center"
                                      spacing={1.5}
                                      sx={{
                                        flexWrap: 'wrap',
                                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                                      }}
                                    >
                                      <Grid size={{ xs: 12, sm: 'auto' }}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            bgcolor: 'white',
                                            border: '1px solid #E0E0E0',
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1,
                                            flexWrap: 'wrap',
                                            width: { xs: '100%', md: 'auto' },
                                            justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              border: '1px solid #E0E0E0',
                                              borderRadius: 2,
                                              px: 1,
                                              py: 0.5,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              width: { xs: '100%', sm: 'auto' },
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {t('checklist.questionsAnswered', {
                                                answered: answeredCount,
                                                total: totalQuestions,
                                              })}
                                            </Typography>
                                            {answeredSummaryComplete ? (
                                              <CheckCircle
                                                sx={{ fontSize: 20, color: '#4caf50' }}
                                              />
                                            ) : (
                                              <CheckCircleOutline
                                                sx={{ fontSize: 20, color: '#A5B2BB' }}
                                              />
                                            )}
                                          </Box>
                                          {/* Progress indicator inside the same box */}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </AccordionSummary>
                              {expandedChecklist === checklist.id && (
                                <AccordionDetails
                                  sx={{
                                    bgcolor: 'white',
                                    p: 1,
                                    mt: 1,
                                    borderRadius: 1,
                                    border: '1px solid #A5B2BB',
                                  }}
                                >
                                  {checklist.questions?.map((pregunta, index: number) => (
                                    <Accordion
                                      key={pregunta.id}
                                      expanded={expandedQuestion === pregunta.id}
                                      onChange={handlePreguntaChange(pregunta.id)}
                                      sx={{
                                        mb: 2,
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                          bgcolor: '#F4F8F8',
                                          borderRadius: 1,
                                          border: '1px solid #A5B2BB',
                                          minHeight: 'auto',
                                          height: 'auto',
                                          '& .MuiAccordionSummary-content': {
                                            alignItems: 'center',
                                            margin: '8px 0 !important',
                                          },
                                          '&.Mui-expanded': {
                                            minHeight: 'auto',
                                            height: 'auto',
                                          },
                                          '& .MuiAccordionSummary-content.Mui-expanded': {
                                            margin: '8px 0 !important',
                                          },
                                        }}
                                      >
                                        <Grid
                                          container
                                          alignItems="center"
                                          justifyContent="space-between"
                                          sx={{ width: '100%', gap: { xs: 1, md: 0 } }}
                                        >
                                          <Grid size={{ xs: 12, md: 11 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                              {index + 1}. {pregunta.text}
                                            </Typography>
                                          </Grid>
                                          <Grid
                                            size={{ xs: 12, md: 1 }}
                                            sx={{
                                              display: 'flex',
                                              justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                            }}
                                          >
                                            {isQuestionComplete(pregunta) ? (
                                              <CheckCircle
                                                sx={{ color: '#4caf50', fontSize: 20 }}
                                              />
                                            ) : (
                                              <CheckCircleOutline
                                                sx={{ color: '#A5B2BB', fontSize: 20 }}
                                              />
                                            )}
                                          </Grid>
                                        </Grid>
                                      </AccordionSummary>
                                      {expandedQuestion === pregunta.id && (
                                        <AccordionDetails
                                          sx={{
                                            bgcolor: 'white',
                                            p: 2,
                                            mt: 1,
                                            borderRadius: 1,
                                            border: '1px solid #A5B2BB',
                                          }}
                                        >
                                          <Grid container spacing={2}>
                                            {/* Respuesta (fila completa) */}
                                            <Grid size={12}>
                                              <Typography
                                                variant="body2"
                                                sx={{ mb: 0.5, fontWeight: 600 }}
                                              >
                                                {t('checklist.selectAnswer')}
                                              </Typography>
                                              <Select
                                                fullWidth
                                                disabled={!!readOnlyMode}
                                                value={pregunta.selectedOptionId ?? ''}
                                                onChange={(e) => {
                                                  const raw = e.target.value as unknown as
                                                    | string
                                                    | number;
                                                  const isEmpty = String(raw) === '';
                                                  handleRespuestaChange(
                                                    checklist.id,
                                                    pregunta.id,
                                                    isEmpty ? '' : Number(raw),
                                                  );
                                                }}
                                                displayEmpty
                                                size="small"
                                              >
                                                <MenuItem value="">
                                                  <em>{t('common.select')}</em>
                                                </MenuItem>
                                                {pregunta.options.map((opt) => (
                                                  <MenuItem key={opt.id} value={opt.id}>
                                                    {opt.label}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </Grid>

                                            {/* Comentario */}
                                            <Grid size={{ xs: 12, md: 8 }}>
                                              <Typography
                                                variant="body2"
                                                sx={{ mb: 0.5, fontWeight: 600 }}
                                              >
                                                {t('checklist.comment')}
                                              </Typography>
                                              <TextField
                                                fullWidth
                                                multiline
                                                required
                                                disabled={!!readOnlyMode}
                                                rows={5}
                                                value={pregunta.comment}
                                                onChange={(e) =>
                                                  handleComentarioChange(
                                                    checklist.id,
                                                    pregunta.id,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder={t('defineActivities.enterText')}
                                                size="small"
                                              />
                                            </Grid>

                                            {/* Evidencia */}
                                            <Grid size={{ xs: 12, md: 4 }}>
                                              <Typography
                                                variant="body2"
                                                sx={{ mb: 0.5, fontWeight: 600 }}
                                              >
                                                {t('checklist.attachEvidence')}
                                              </Typography>
                                              {/* Cuando ya existe evidencia */}
                                              {pregunta.evidenceName ? (
                                                <Box
                                                  sx={{
                                                    border: '1px solid #d5e2e7',
                                                    borderRadius: 1,
                                                    p: 1.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    bgcolor: '#fafcfd',
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 1,
                                                      flexWrap: 'wrap',
                                                    }}
                                                  >
                                                    <Badge badgeContent={1} color="primary">
                                                      <AttachFile fontSize="small" />
                                                    </Badge>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ flex: 1, wordBreak: 'break-all' }}
                                                    >
                                                      {pregunta.evidenceName}
                                                    </Typography>
                                                  </Box>
                                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      disabled={
                                                        !pregunta.evidenceUrl || isAnyLoading
                                                      }
                                                      onClick={() =>
                                                        pregunta.evidenceUrl &&
                                                        window.open(pregunta.evidenceUrl, '_blank')
                                                      }
                                                      sx={{ flex: 1, textTransform: 'none' }}
                                                    >
                                                      {t('common.view')}
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      color="error"
                                                      variant="outlined"
                                                      disabled={
                                                        (readOnlyMode ?? false) || isAnyLoading
                                                      }
                                                      onClick={() =>
                                                        handleDeleteEvidencias(
                                                          checklist.id,
                                                          pregunta.id,
                                                        )
                                                      }
                                                      sx={{ flex: 1, textTransform: 'none' }}
                                                    >
                                                      {t('common.delete')}
                                                    </Button>
                                                  </Box>
                                                </Box>
                                              ) : !readOnlyMode ? (
                                                <Box
                                                  onDragOver={(e) => e.preventDefault()}
                                                  onDrop={(e) => {
                                                    e.preventDefault();
                                                    if (e.dataTransfer?.files?.length) {
                                                      handleFileUpload(
                                                        checklist.id,
                                                        pregunta.id,
                                                        e.dataTransfer.files,
                                                      );
                                                    }
                                                  }}
                                                  sx={{
                                                    border: '1px dashed #00BCE4',
                                                    borderRadius: 1,
                                                    p: 2,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    bgcolor: '#ffffff',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1,
                                                    '&:hover': {
                                                      bgcolor: '#f2fbfd',
                                                    },
                                                  }}
                                                  onClick={() => {
                                                    const input = document.createElement('input');
                                                    input.type = 'file';
                                                    input.onchange = (ev: Event) => {
                                                      const target = ev.target as HTMLInputElement;
                                                      handleFileUpload(
                                                        checklist.id,
                                                        pregunta.id,
                                                        target.files,
                                                      );
                                                    };
                                                    input.click();
                                                  }}
                                                >
                                                  <AttachFile
                                                    sx={{ fontSize: 32, color: '#00BCE4' }}
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: '#555', px: 1 }}
                                                  >
                                                    {tr('checklist.dropFileHereOrBrowse')}
                                                  </Typography>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      borderColor: '#00BCE4',
                                                      color: '#00BCE4',
                                                      textTransform: 'none',
                                                      px: 2,
                                                      '&:hover': { borderColor: '#009bbd' },
                                                    }}
                                                  >
                                                    {t('common.select')}
                                                  </Button>
                                                </Box>
                                              ) : (
                                                <Typography
                                                  variant="body2"
                                                  sx={{ fontStyle: 'italic', color: '#999' }}
                                                >
                                                  {t('checklist.readOnlyNoEvidenceUpload')}
                                                </Typography>
                                              )}
                                            </Grid>
                                          </Grid>
                                        </AccordionDetails>
                                      )}
                                    </Accordion>
                                  ))}
                                  {customFieldList.length > 0 && (
                                    <Accordion
                                      key={`${checklist.id}-custom-fields`}
                                      expanded={expandedCustomFields[checklist.id] ?? false}
                                      onChange={handleCustomFieldsAccordionChange(checklist.id)}
                                      sx={{
                                        mb: 2,
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                          bgcolor: '#F4F8F8',
                                          borderRadius: 1,
                                          border: '1px solid #A5B2BB',
                                          minHeight: 'auto',
                                          height: 'auto',
                                          '& .MuiAccordionSummary-content': {
                                            alignItems: 'center',
                                            margin: '8px 0 !important',
                                          },
                                          '&.Mui-expanded': {
                                            minHeight: 'auto',
                                            height: 'auto',
                                          },
                                          '& .MuiAccordionSummary-content.Mui-expanded': {
                                            margin: '8px 0 !important',
                                          },
                                        }}
                                      >
                                        <Grid
                                          container
                                          alignItems="center"
                                          justifyContent="space-between"
                                          sx={{ width: '100%' }}
                                        >
                                          <Grid size={{ xs: 12, md: 11 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {(checklist.questions?.length ?? 0) + 1}.{' '}
                                              {checklist.typeName}
                                            </Typography>
                                          </Grid>
                                          <Grid
                                            size={{ xs: 12, md: 1 }}
                                            sx={{
                                              display: 'flex',
                                              justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                            }}
                                          >
                                            {customFieldsComplete ? (
                                              <CheckCircle
                                                sx={{ color: '#4caf50', fontSize: 20 }}
                                              />
                                            ) : (
                                              <CheckCircleOutline
                                                sx={{ color: '#A5B2BB', fontSize: 20 }}
                                              />
                                            )}
                                          </Grid>
                                        </Grid>
                                      </AccordionSummary>
                                      {(expandedCustomFields[checklist.id] ?? false) && (
                                        <AccordionDetails
                                          sx={{
                                            bgcolor: 'white',
                                            p: 2,
                                            mt: 1,
                                            borderRadius: 1,
                                            border: '1px solid #A5B2BB',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: 2,
                                            }}
                                          >
                                            {orderedCustomFieldGroups.map(({ sort, fields }) => {
                                              const hasMultiple = fields.length > 1;
                                              const columnSize =
                                                hasMultiple && fields.length > 0
                                                  ? Math.max(Math.floor(12 / fields.length), 2)
                                                  : 12;
                                              return (
                                                <Grid
                                                  container
                                                  spacing={2}
                                                  key={`custom-field-group-${sort}`}
                                                >
                                                  {fields.map((field) => {
                                                    const trimmedValue =
                                                      `${field.value ?? ''}`.trim();
                                                    const numericValue =
                                                      trimmedValue === ''
                                                        ? NaN
                                                        : Number(trimmedValue);
                                                    const isNumeric = Number.isFinite(numericValue);
                                                    const isNegative =
                                                      isNumeric && numericValue < 0;
                                                    const fieldHasError =
                                                      !readOnlyMode &&
                                                      !isCustomFieldValueComplete(field);
                                                    const helperText =
                                                      fieldHasError && !readOnlyMode
                                                        ? !trimmedValue || !isNumeric
                                                          ? t('checklist.customFieldRequired')
                                                          : isNegative
                                                            ? t('checklist.customFieldNonNegative')
                                                            : t('checklist.customFieldRequired')
                                                        : undefined;
                                                    return (
                                                      <Grid
                                                        key={field.id}
                                                        size={{
                                                          xs: 12,
                                                          md: hasMultiple ? columnSize : 12,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                                        >
                                                          {field.name}
                                                        </Typography>
                                                        <TextField
                                                          type="number"
                                                          fullWidth
                                                          value={field.value}
                                                          onChange={(e) =>
                                                            handleCustomFieldChange(
                                                              checklist.id,
                                                              field.id,
                                                              e.target.value,
                                                            )
                                                          }
                                                          placeholder={t('common.enterNumber')}
                                                          size="small"
                                                          disabled={!!readOnlyMode}
                                                          inputProps={{ step: 'any', min: 0 }}
                                                          required
                                                          error={fieldHasError}
                                                          helperText={helperText}
                                                        />
                                                      </Grid>
                                                    );
                                                  })}
                                                </Grid>
                                              );
                                            })}
                                          </Box>
                                        </AccordionDetails>
                                      )}
                                    </Accordion>
                                  )}
                                </AccordionDetails>
                              )}
                            </Accordion>
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}

            {/* Action Buttons */}
            <Navigation
              onBack={handleBack}
              backTitle={t('common.backToStep1')}
              onExit={handleExit}
              exitTitle={t('common.exit')}
              disableNext={disableNext}
              disableReason={disableNextReason}
              onSaveDraft={readOnlyMode ? () => {} : handleSaveProgress}
              saveDraftTitle={t('common.saveProgress')}
              onNext={() => handleContinue()}
              nextTitle={
                readOnlyMode ? t('common.continue') : t('checklist.finishAndSendForApproval')
              }
              readOnlyMode={readOnlyMode ?? false}
              isAnyLoading={isAnyLoading}
              disableSaveDraft={false}
            />
          </Box>
        </Paper>
      </Box>

      {/* Modal de confirmación */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 0,
            pt: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('checklist.confirmCloseUp')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning
              sx={{
                fontSize: 64,
                color: '#ffa726',
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#333',
              }}
            >
              {t('checklist.confirmCloseMessage')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                lineHeight: 1.6,
                maxWidth: '80%',
              }}
            >
              {t('checklist.confirmSubmissionWarning')}
            </Typography>
            {/* Fulfillment progress summary */}
            {focusesProgress.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  bgcolor: '#F4F8F8',
                  border: '1px solid #A5B2BB',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 260,
                  overflowY: 'auto',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                  {t('ceoValidation.weightedCompliance')}:{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#79FCBD',
                      px: 1,
                      borderRadius: 1,
                    }}
                  >
                    {formatPercentage(globalFulfillmentPct, 1)}%
                  </Box>
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
                  {focusesProgress.map((f, idx) => (
                    <Box
                      key={f.APFocusId}
                      component="li"
                      sx={{ mb: 0.5, pr: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
                        {idx + 1}. {f.Name}:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: '#000' }}
                        title={t('ceoValidation.fulfillmentProgressTooltip') || undefined}
                      >
                        {formatPercentage(f.FulfillmentProgressPct ?? 0, 1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mt: 1 }}>
                  {t('checklist.resultsDisclaimer')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
            px: 4,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={() => setShowConfirmModal(false)}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading}
            onClick={handleConfirmSave}
            sx={{
              bgcolor: '#00BCE4',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 150,
              '&:hover': { bgcolor: '#00acc1' },
            }}
          >
            {t('defineActivities.finishAndSent')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Checklist;
